import { AllCurrencies, Invoice, WalletAddress } from "./Billing";
import { Customer } from "./Customer";
import { Provider } from "./Provider";
import { Manager } from "@lib/types/Manager";
import { RcFile } from "antd/lib/upload";

export type TransactionAttachment = {
  createdAt: string;
  createdBy: string;
  deletedAt: string | null;
  description: string;
  expiresAt: string | null;
  fileId: string;
  filePath: string;
  id: string;
  mimeType: string;
  originalFileName: string;
  token: string;
  transactionId: string;
  updatedAt: string;
  updatedBy: string;
  url: string;
  size?: number;

  managerId: string | null;
  manager?: Manager;
};

export enum TransactionAttachmentStatus {
  Done = "done",
  Uploading = "uploading",
  Error = "error",
}

export interface TransactionAttachmentFile extends RcFile {
  data?: TransactionAttachment;
  status: TransactionAttachmentStatus;
  progress?: number;
  error?: string;
  previewUrl?: string;
  originalFileName?: string;
  manager?: Manager;
}

export type ScannedTransaction = {
  id: string;

  customerId: string | null;
  customer?: Customer;

  walletAddressId: string;
  walletAddress?: WalletAddress;

  txId: string;
  amount: string;
  confirmed: string;
  confirmedDate: string;
  fromWalletAddress: string;
  tokenType: string;

  contractType: string;
  convertedAmount: string | null;
  convertedRate: string | null;
  convertedTo: AllCurrencies | null;

  extraData?: {
    transactionDetailUrl?: string;
    transaction_id?: string;
  };

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export type ProviderTransactionConfigType = "bank_account" | "card";

export type ProviderTransactionConfigConfig = {
  type: ProviderTransactionConfigType;
} & (
  | {
      type: "bank_account";
      accountNumber: string;
      accountName: string;
      bankName: string;
    }
  | {
      type: "card";
      cardNumber: string;
      cardExpiry: string;
      cardCvv: string;
      cardHolderName: string;
    }
);

export type ProviderTransactionConfig = {
  id: string;
  name: string;
  currency: CurrencyEnum;
  from: number;
  to: number;
  dailyLimit: number;
  providerId: string | null;
  provider?: Provider;
  lastUsedAt?: string | null;
  dailyTotal?: number;
  config: ProviderTransactionConfigConfig;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
};

export type TransactionType = {
  id: string;
  amount: number;
  convertedAmount: number | null;
  convertedRate: number | null;
  convertedTo: CurrencyEnum | null;
  currency: CurrencyEnum;
  status: TransactionProxyStatus;
  state: TransactionProxyState | null;
  error: string;
  customerId: string;
  providerId: string;
  referenceCallbackUrl: string;
  extraData: Record<string, any>;
  rawRequest: Record<string, any>;
  provider: Provider;
  customer: Customer;
  externalId: string;
  providerTransactionConfigId: string | null;
  providerTransactionConfig?: ProviderTransactionConfig;
  logs?: TransactionTypeLog[];
  attachments?: TransactionAttachment[];

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export enum TransactionTypeLogType {
  Viewed = "viewed",
  Created = "created",
  Updated = "updated",
  Deleted = "deleted",
  Copied = "copied",
  Failed = "failed",
  Document = "document",
}

export type TransactionTypeLog = {
  id: string;
  transactionId: string;
  comment: string;
  type: TransactionTypeLogType;
  managerId: string | null;
  manager?: Manager;
  extraData: Record<string, any>;

  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
};

export type ManualTransaction = {
  id: string;

  amount: number;
  description: string | null;
  currency: CurrencyEnum;
  convertedAmount: string | null;
  convertedRate: string | null;
  convertedTo: AllCurrencies;

  customerId: string | null;
  customer?: Customer;

  invoiceId: string | null;
  invoice?: Invoice;

  extraData: Record<string, any>;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export enum CurrencyEnum {
  USD = "USD",
  EUR = "EUR",
  GBP = "GBP",
  JPY = "JPY",
  CHF = "CHF",
  RUR = "RUR",
  CAD = "CAD",
  TRY = "TRY",
  SEK = "SEK",
  CNY = "CNY",
  BYN = "BYN",
  MYR = "MYR",
  VND = "VND",
  THB = "THB",
  SGD = "SGD",
  IND = "IND",
  IDR = "IDR",
  BRL = "BRL",
  INR = "INR",
  HKD = "HKD",
  MXN = "MXN",
  ARS = "ARS",
  COP = "COP",
  CLP = "CLP",
  PEN = "PEN",
  ECS = "ECS",
  BOB = "BOB",
  GTQ = "GTQ",
  CRC = "CRC",
  RUB = "RUB",
  PKR = "PKR",
  KRW = "KRW",
  KZT = "KZT",
  UZS = "UZS",
  UAH = "UAH",
  AZN = "AZN",
  BDT = "BDT",
  BHD = "BHD",
  BIF = "BIF",
  BND = "BND",
  BWP = "BWP",
  BZD = "BZD",
  BMD = "BMD",
}

// export enum TransactionStatusEnum {
//   Initialized = "Initialized",
//   Pending = "Pending",
//   FailedByProvider = "FailedByProvider",
//   FailedBySystem = "FailedBySystem",
//   Confirmed = "confirmed",
//   AmountNotMatch = "amount_not_match",
//   ReferenceError = "ReferenceError",
//   Success = "Success",
//   Failed = "Failed",
//   PendingProviderApproval = "PendingProviderApproval",
//   ApprovedByProvider = "ApprovedByProvider",
//   PendingForAccountInfo = "PendingForAccountInfo",
//   ApprovedByCustomer = "ApprovedByCustomer",
//   CanceledByCustomer = "CanceledByCustomer",
//   CanceledByProvider = "CanceledByProvider",
//   Canceled = "Canceled",
// }

export enum TransactionProxyStatus {
  Initialized = "Initialized",
  Pending = "Pending",
  Confirmed = "confirmed",
  AmountNotMatch = "amount_not_match",
  ReferenceError = "ReferenceError",
  Success = "Success",
  Failed = "Failed",
  Canceled = "Canceled",
}

export enum TransactionProxyState {
  PendingProviderApproval = "PendingProviderApproval",
  ApprovedByProvider = "ApprovedByProvider",
  PendingForAccountInfo = "PendingForAccountInfo",
  ApprovedByCustomer = "ApprovedByCustomer",
  CanceledByCustomer = "CanceledByCustomer",
  CanceledByProvider = "CanceledByProvider",
  FailedByProvider = "FailedByProvider",
  FailedBySystem = "FailedBySystem",
  PendingCustomerApproval = "PendingCustomerApproval",
}
